import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Checkbox,
  ListItem
} from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ReportList, ReportListItem } from './components'
import PropTypes from 'prop-types'
import { successConfirm } from '../success-confirmation'

export const ReportsModal = ({ reportOptions }) => {
  const [withSearchParams, setWithSearchParams] = useState(true)
  const [reportExtraOptions, setReportExtraOptions] = useState({
    employee_contract_attachments: {
      showTerminatedContracts: false,
      terminatedContractRecurrences: ['once', 'monthly', 'yearly', 'termination']
    }
  })
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialSelectedColumns = useMemo(() => Object.fromEntries(
    reportOptions
      .filter((reportOption) => (reportOption?.columns ?? []).length > 0)
      .map((reportOption) => [
        reportOption.value,
        reportOption.columns.map((column) => column.key)
      ])
  ), [reportOptions])

  const [selectedReport, setSelectedReport] = useState(reportOptions[0])
  const [selectedColumns, setSelectedColumns] = useState(initialSelectedColumns)

  useEffect(() => {
    setSelectedColumns((previousSelectedColumns) => {
      const nextSelectedColumns = { ...previousSelectedColumns }

      Object.entries(initialSelectedColumns).forEach(([reportValue, columnKeys]) => {
        if (!Array.isArray(nextSelectedColumns[reportValue])) {
          nextSelectedColumns[reportValue] = columnKeys
        }
      })

      return nextSelectedColumns
    })
  }, [initialSelectedColumns])

  useEffect(() => {
    if (!reportOptions.some((reportOption) => reportOption.value === selectedReport?.value)) {
      setSelectedReport(reportOptions[0])
    }
  }, [reportOptions, selectedReport])

  const handleSelectedReport = (event) => {
    setSelectedReport(
      reportOptions.find(
        (reportOption) => reportOption.value === event.target.id
      )
    )
  }

  const onConfirm = async () => {
    let params = {}

    if (withSearchParams) {
      params = { ...Object.fromEntries(searchParams), ...params }
    }

    if (selectedReport.columns.length) {
      params.columns = selectedColumns[selectedReport.value] ?? []
    }

    if (selectedReport?.supportsTerminatedContractFilters) {
      params.show_expired_contracts = reportExtraOptions.employee_contract_attachments.showTerminatedContracts
      params.expired_contract_recurrences =
        reportExtraOptions.employee_contract_attachments.terminatedContractRecurrences
    }

    try {
      await axios.post(`/api/v1/reports/${selectedReport.value}`, params)
    } catch (e) {
      console.log(e)
    } finally {
      navigate(-1)
      await successConfirm({
        title: 'Descarga de datos en proceso',
        description:
          'Dentro de los próximos 15 minutos, recibirás un correo electrónico con el archivo adjunto.'
      })
    }
  }

  const handleSelectedColumns = (evt) => {
    const isChecked = evt.target.checked
    const columnKey = evt.target.id

    if (isChecked) {
      setSelectedColumns({
        ...selectedColumns,
        [selectedReport.value]: [
          ...(selectedColumns[selectedReport.value] ?? []),
          columnKey
        ]
      })
      return
    }

    const newSelectedColumns = (selectedColumns[selectedReport.value] ?? []).filter(
      (selectedColumn) => selectedColumn !== columnKey
    )

    setSelectedColumns({
      ...selectedColumns,
      [selectedReport.value]: newSelectedColumns
    })
  }

  const handleToggleShowTerminatedContracts = (evt) => {
    setReportExtraOptions({
      ...reportExtraOptions,
      employee_contract_attachments: {
        ...reportExtraOptions.employee_contract_attachments,
        showTerminatedContracts: evt.target.checked
      }
    })
  }

  const handleTerminatedContractRecurrence = (evt) => {
    const recurrence = evt.target.id
    const isChecked = evt.target.checked
    const selectedRecurrences = reportExtraOptions.employee_contract_attachments.terminatedContractRecurrences

    setReportExtraOptions({
      ...reportExtraOptions,
      employee_contract_attachments: {
        ...reportExtraOptions.employee_contract_attachments,
        terminatedContractRecurrences: isChecked
          ? [...selectedRecurrences, recurrence]
          : selectedRecurrences.filter((selectedRecurrence) => selectedRecurrence !== recurrence)
      }
    })
  }

  return (
    <Dialog open size='lg' className='p-6 max-h-full  overflow-y-auto'>
      <DialogHeader className='flex flex-col items-start'>
        Descarga de tablas
        <Typography className='text-gray-500'>
          Selecciona que tabla quieres descargar. El documento generado será
          enviado a tu correo.
        </Typography>
      </DialogHeader>

      <DialogBody className='flex flex-col gap-3'>
        <ReportList>
          {reportOptions.map((reportOption) => (
            <ReportListItem
              active={selectedReport.value === reportOption.value}
              key={reportOption.value}
              id={reportOption.value}
              label={reportOption.label}
              description={reportOption.description}
              icon={reportOption.icon}
              checked={selectedReport.value === reportOption.value}
              name='selected-report'
              onChange={handleSelectedReport}
            />
          ))}
        </ReportList>
        {Boolean((selectedReport?.columns ?? []).length) && (
          <div>
            <Typography className='text-black font-medium py-4'>
              Selección de columnas
            </Typography>

            <div className=''>
              {selectedReport.columns.map((column) => (
                <div key={column.key} className='inline-block mr-4'>
                  <Checkbox
                    containerProps={{
                      className:
                        'relative flex items-center cursor-pointer p-1 rounded-full'
                    }}
                    key={column.key}
                    id={column.key}
                    checked={(selectedColumns[selectedReport.value] ?? []).includes(
                      column.key
                    )}
                    label={column.label}
                    onChange={handleSelectedColumns}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedReport?.supportsTerminatedContractFilters && (
          <ListItem className='border' ripple={false}>
            <div className='w-full'>
              <Checkbox
                className='h-4 w-4'
                onChange={handleToggleShowTerminatedContracts}
                checked={reportExtraOptions.employee_contract_attachments.showTerminatedContracts}
                label={
                  <div>
                    <Typography color='blue-gray' className='font-medium text-sm'>
                      Mostrar documentos de contratos terminados
                    </Typography>
                    <Typography className='font-normal text-sm text-gray-500'>
                      Incluye documentos de contratos terminados entre persona y contratista.
                    </Typography>
                  </div>
                }
                containerProps={{
                  className: '-mt-5'
                }}
              />

              <div className='mt-3 grid grid-cols-2 gap-y-2 pl-10'>
                {[
                  ['once', 'De una vez'],
                  ['monthly', 'Mensuales'],
                  ['yearly', 'Anuales'],
                  ['termination', 'De término']
                ].map(([value, label]) => (
                  <Checkbox
                    key={value}
                    id={value}
                    className='h-4 w-4'
                    disabled={!reportExtraOptions.employee_contract_attachments.showTerminatedContracts}
                    checked={reportExtraOptions.employee_contract_attachments.terminatedContractRecurrences.includes(value)}
                    label={label}
                    onChange={handleTerminatedContractRecurrence}
                    containerProps={{
                      className: 'p-0'
                    }}
                  />
                ))}
              </div>
            </div>
          </ListItem>
        )}
        <ListItem className='border' ripple={false}>
          <Checkbox
            className='h-4 w-4'
            onChange={(evt) => setWithSearchParams(evt.target.checked)}
            checked={withSearchParams}
            label={
              <div>
                <Typography color='blue-gray' className='font-medium text-sm'>
                  Aplicar filtros al archivo
                </Typography>
                <Typography className='font-normal text-sm text-gray-500'>
                  Selecciona esta opción para descargar el archivo con los
                  filtros aplicados a la tabla.
                </Typography>
              </div>
            }
            containerProps={{
              className: '-mt-5'
            }}
          />
        </ListItem>
      </DialogBody>
      <DialogFooter className='flex justify-between'>
        <Button onClick={() => navigate(-1)} variant='outlined' color='white'>
          Cancelar
        </Button>

        <Button onClick={onConfirm}>Enviar archivo</Button>
      </DialogFooter>
    </Dialog>
  )
}

ReportsModal.propTypes = {
  previousPath: PropTypes.string,
  reportOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      value: PropTypes.string,
      icon: PropTypes.elementType,
      supportsTerminatedContractFilters: PropTypes.bool,
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          label: PropTypes.string
        })
      )
    })
  )
}
